import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Save, Loader2, AlertCircle, Calendar, Users, X, Plus,
  FileText, ClipboardList, ChevronRight, ChevronLeft, Info
} from 'lucide-react';

import { useToast, ToastContainer } from '../common/Toast';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import FormStepper from '../common/FormStepper';
import VendorService from '../../services/vendorService';
import SourcingRfpService from '../../services/sourcingRFPService';

// ────────────────────────────────────────────────────────────────────
// STEP DEFINITIONS
// ────────────────────────────────────────────────────────────────────
const STEPS = [
  { id: 0, label: 'RFP Details', icon: FileText, hint: 'Basic identification and scope' },
  { id: 1, label: 'Requirements', icon: ClipboardList, hint: 'Line items and pricing details' },
  { id: 2, label: 'Vendor Selection', icon: Users, hint: 'Invite vendors to bid' },
  { id: 3, label: 'Timeline', icon: Calendar, hint: 'Deadlines and submission' },
];

//////////////////////////////////////////////////////////////
// EMBEDDED STYLES
//////////////////////////////////////////////////////////////

// Cleaner styles using CSS variables where possible
const styles = {
  pageContainer: {
    padding: '24px',
  },
  card: {
    background: 'white',
    border: '1px solid var(--border-color)',
    borderRadius: '0',
    boxShadow: 'var(--shadow-sm)',
    overflow: 'hidden',
  },
  cardBody: {
    padding: '32px',
  },
  formGroup: {
    marginBottom: '24px',
  },
  formLabel: {
    display: 'block',
    marginBottom: '8px',
    fontWeight: 500,
    fontSize: '14px',
    color: 'var(--text-primary)',
    fontFamily: 'var(--font-family)',
  },
  required: {
    color: '#dc2626',
    marginLeft: '4px',
  },
  formControl: {
    // Styles moved to .form-control in theme.css
  },
  textarea: {
    resize: 'vertical',
    minHeight: '120px',
  },
  errorMessage: {
    display: 'flex',
    alignItems: 'center',
    marginTop: '6px',
    fontSize: '13px',
    color: '#dc2626',
  },
  infoBanner: {
    display: 'flex',
    alignItems: 'center',
    padding: '16px 24px',
    backgroundColor: 'var(--accent-color)',
    border: '1px solid var(--border-color)',
    borderRadius: '0',
    fontSize: '14px',
    color: 'var(--text-primary)',
    marginBottom: '24px',
  },
  multiSelectContainer: {
    position: 'relative',
  },
  multiSelectTrigger: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    minHeight: '44px',
    padding: '8px 14px',
    backgroundColor: '#fff',
    border: '1px solid var(--border-color)',
    borderRadius: '0',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  },
  multiSelectDropdown: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    marginTop: '4px',
    backgroundColor: 'white',
    border: '1px solid var(--border-color)',
    borderRadius: '0',
    boxShadow: 'var(--shadow-lg)',
    zIndex: 1000,
    maxHeight: '300px',
    overflow: 'hidden',
  },
  searchBox: {
    padding: '12px',
    borderBottom: '1px solid var(--border-color)',
  },
  searchInput: {
    width: '100%',
    padding: '8px 12px',
    fontSize: '14px',
    outline: 'none',
    fontFamily: 'var(--font-family)',
  },
  option: {
    display: 'flex',
    alignItems: 'center',
    padding: '10px 16px',
    cursor: 'pointer',
    transition: 'background-color 0.15s ease',
    fontSize: '14px',
    fontFamily: 'var(--font-family)',
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '4px 10px',
    backgroundColor: 'var(--gold-primary)',
    color: 'white',
    borderRadius: '0',
    fontSize: '13px',
    fontWeight: 500,
    marginRight: '6px',
    marginBottom: '6px',
  },
  spinning: {
    animation: 'spin 1s linear infinite',
  },
};

// Add keyframes for spinning animation
const styleSheet = `
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = styleSheet;
  if (!document.head.querySelector('style[data-rfp-styles]')) {
    style.setAttribute('data-rfp-styles', 'true');
    document.head.appendChild(style);
  }
}

//////////////////////////////////////////////////////////////
// MULTI-SELECT DROPDOWN COMPONENT
//////////////////////////////////////////////////////////////

const MultiSelectDropdown = ({ 
  options = [], 
  selected = [], 
  onChange, 
  placeholder = 'Select vendors',
  label,
  required = false,
  error = null,
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [hoveredIndex, setHoveredIndex] = useState(null);

  const filteredOptions = options.filter(opt =>
    opt.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const toggleOption = (optionId) => {
    if (disabled) return;
    if (selected.includes(optionId)) {
      onChange(selected.filter(id => id !== optionId));
    } else {
      onChange([...selected, optionId]);
    }
  };

  const removeVendor = (e, vendorId) => {
    e.stopPropagation();
    onChange(selected.filter(id => id !== vendorId));
  };

  const selectedVendors = options.filter(opt => selected.includes(opt._id));

  return (
    <div style={styles.formGroup}>
      {label && (
        <label style={styles.formLabel}>
          {label}
          {required && <span style={styles.required}>*</span>}
        </label>
      )}
      <div style={styles.multiSelectContainer}>
        <div
          style={{
            ...styles.multiSelectTrigger,
            ...(error ? styles.multiSelectTriggerError : {}),
            ...(disabled ? styles.multiSelectTriggerDisabled : {}),
          }}
          onClick={() => !disabled && setIsOpen(!isOpen)}
        >
          <div style={{ display: 'flex', flexWrap: 'wrap', flex: 1, gap: '4px' }}>
            {selectedVendors.length === 0 ? (
              <span style={styles.placeholder}>{placeholder}</span>
            ) : (
              selectedVendors.map(vendor => (
                <span key={vendor._id} style={styles.badge}>
                  {vendor.name}
                  {!disabled && (
                    <X
                      size={14}
                      style={styles.badgeRemove}
                      onClick={(e) => removeVendor(e, vendor._id)}
                    />
                  )}
                </span>
              ))
            )}
          </div>
          <Users size={16} style={{ color: '#9ca3af', marginLeft: '8px', flexShrink: 0 }} />
        </div>

        {isOpen && !disabled && (
          <div style={styles.multiSelectDropdown}>
            <div style={styles.searchBox}>
              <input
                type="text"
                className="form-control"
                placeholder="Search vendors..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
            <div style={styles.optionsList}>
              {filteredOptions.length === 0 ? (
                <div style={styles.emptyState}>
                  No vendors found
                </div>
              ) : (
                filteredOptions.map((option, index) => (
                  <div
                    key={option._id}
                    style={{
                      ...styles.option,
                      ...(hoveredIndex === index ? styles.optionHover : {}),
                    }}
                    onClick={() => toggleOption(option._id)}
                    onMouseEnter={() => setHoveredIndex(index)}
                    onMouseLeave={() => setHoveredIndex(null)}
                  >
                    <input
                      type="checkbox"
                      style={styles.checkbox}
                      checked={selected.includes(option._id)}
                      onChange={() => { }}
                    />
                    <span>{option.name}</span>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {error && (
          <div style={styles.errorMessage}>
            <AlertCircle size={14} style={{ marginRight: '4px' }} />
            {error}
          </div>
        )}
      </div>

      {/* Backdrop to close dropdown */}
      {isOpen && !disabled && (
        <div
          style={styles.backdrop}
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};

//////////////////////////////////////////////////////////////
// MAIN CREATE RFP COMPONENT
//////////////////////////////////////////////////////////////

const CreateRfp = () => {
  const navigate = useNavigate();
  const { success, error: showError, toasts, removeToast } = useToast();

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    scope: '',
    invitedVendors: [],
    lineItems: [{ description: '', requirement: '' }],
    responseDeadline: '',
  });

  // UI state
  const [currentStep, setCurrentStep] = useState(0);
  const [vendors, setVendors] = useState([]);
  const [loadingVendors, setLoadingVendors] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [focusedField, setFocusedField] = useState(null);
  
  //////////////////////////////////////////////////////////////
  // LOAD VENDORS
  //////////////////////////////////////////////////////////////

  useEffect(() => {
    loadVendors();
  }, []);

  const loadVendors = async () => {
    setLoadingVendors(true);
    try {
      const response = await VendorService.getVendors({
        limit: 1000, // important for dropdowns
      });

      const vendorList =
        response?.data?.vendors ||
        response?.data ||
        response?.vendors ||
        [];

      setVendors(vendorList);

    } catch (err) {
      console.error("Vendor load error:", err);
      showError(err.message || 'Failed to load vendors');
    } finally {
      setLoadingVendors(false);
    }
  };

  const addLineItem = () => {
  setFormData(prev => ({
    ...prev,
    lineItems: [...prev.lineItems, { description: '', requirement: '' }]
  }));
};

  const removeLineItem = (index) => {
    const updatedLines = formData.lineItems.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, lineItems: updatedLines }));
  };

  const handleLineItemChange = (index, field, value) => {
    const updatedLines = [...formData.lineItems];
    updatedLines[index][field] = value;
    setFormData(prev => ({ ...prev, lineItems: updatedLines }));
  };
  //////////////////////////////////////////////////////////////
  // FORM HANDLERS
  //////////////////////////////////////////////////////////////

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  //////////////////////////////////////////////////////////////
  // VALIDATION
  //////////////////////////////////////////////////////////////

  const validate = () => {
    const newErrors = {};

    if (!formData.title?.trim()) {
      newErrors.title = 'Title is required';
    }

    if (formData.invitedVendors.length === 0) {
      newErrors.invitedVendors = 'At least one vendor must be selected';
    }

    if (!formData.responseDeadline) {
      newErrors.responseDeadline = 'Response deadline is required';
    } else {
      const deadline = new Date(formData.responseDeadline);
      const now = new Date();
      now.setHours(0, 0, 0, 0);
      if (deadline < now) {
        newErrors.responseDeadline = 'Deadline must be in the future';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  //////////////////////////////////////////////////////////////
  // SUBMIT
  //////////////////////////////////////////////////////////////

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setSubmitting(true);

    try {
      // ADD lineItems TO THE PAYLOAD BELOW
      await SourcingRfpService.createRfp({
        title: formData.title.trim(),
        scope: formData.scope.trim(),
        invitedVendors: formData.invitedVendors,
        lineItems: formData.lineItems, // <--- ADD THIS LINE
        responseDeadline: formData.responseDeadline,
        status: 'DRAFT',
      });

      success('RFP created successfully');
      navigate('/sourcing/rfps');
    } catch (err) {
      showError(err.message || 'Failed to create RFP');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/sourcing/rfps');
  };
  
  //////////////////////////////////////////////////////////////
  // PAGE HEADER ACTIONS
  //////////////////////////////////////////////////////////////

  const headerActions = [
    {
      label: 'Cancel',
      variant: 'outline',
      onClick: handleCancel,
      disabled: submitting,
    },
    {
      label: submitting ? 'Creating...' : 'Create RFP',
      icon: submitting ? <Loader2 size={18} style={styles.spinning} /> : <Save size={18} />,
      variant: 'primary',
      onClick: handleSubmit,
      disabled: submitting,
    },
  ];

  

  const handleNext = (e) => {
    if (e) e.preventDefault();
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(s => s + 1);
      window.scrollTo(0, 0);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(s => s - 1);
      window.scrollTo(0, 0);
    }
  };

  const handleStepClick = (idx) => {
    if (idx <= currentStep) setCurrentStep(idx);
  };

  //////////////////////////////////////////////////////////////
  // PAGE HEADER ACTIONS
  //////////////////////////////////////////////////////////////

  // ─── Step Content Renderers ───────────────────────────────────

  // STEP 0: RFP Details
  const renderStep0 = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <div style={styles.formGroup}>
        <label style={styles.formLabel}>
          RFP Title
          <span style={styles.required}>*</span>
        </label>
        <input
          type="text"
          className="form-control"
          style={{
            borderColor: errors.title ? '#dc2626' : undefined,
          }}
          placeholder="Enter RFP title"
          value={formData.title}
          onChange={(e) => handleChange('title', e.target.value)}
          disabled={submitting}
        />
        {errors.title && (
          <div style={styles.errorMessage}>
            <AlertCircle size={14} style={{ marginRight: '4px' }} />
            {errors.title}
          </div>
        )}
      </div>

      <div style={styles.formGroup}>
        <label style={styles.formLabel}>Scope of Work</label>
        <textarea
          className="form-control"
          style={styles.textarea}
          placeholder="Describe the scope of work, requirements, and deliverables..."
          value={formData.scope}
          onChange={(e) => handleChange('scope', e.target.value)}
          disabled={submitting}
        />
      </div>
    </div>
  );

  // STEP 1: Requirements / Line Items
  const renderStep1 = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <div>
        <h4 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '8px', color: 'var(--text-primary)' }}>
          Line Items / Requirements
        </h4>
        <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '16px' }}>
          Add the specific items or services you want vendors to provide pricing for.
        </p>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {formData.lineItems.map((item, index) => (
          <div key={index} style={{
            display: 'flex',
            gap: '12px',
            alignItems: 'flex-start',
            padding: '16px',
            border: 'none',
            position: 'relative',
            marginBottom: '12px'
          }}>
            <div style={{ flex: 2 }}>
              <label style={styles.formLabel}>Item Description</label>
              <input
                type="text"
                className="form-control"
                placeholder="e.g. Laptop Model X"
                value={item.description}
                onChange={(e) => handleLineItemChange(index, 'description', e.target.value)}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={styles.formLabel}>Qty / Requirement</label>
              <input
                type="text"
                className="form-control"
                placeholder="Quantity"
                value={item.requirement}
                onChange={(e) => handleLineItemChange(index, 'requirement', e.target.value)}
              />
            </div>
            {formData.lineItems.length > 1 && (
              <button
                type="button"
                onClick={() => removeLineItem(index)}
                style={{
                  marginTop: '28px',
                  padding: '8px',
                  color: 'var(--text-muted)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'color 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.color = '#dc2626'}
                onMouseLeave={(e) => e.target.style.color = 'var(--text-muted)'}
              >
                <X size={20} />
              </button>
            )}
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={addLineItem}
        style={{
          alignSelf: 'flex-start',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          color: 'var(--gold-primary)',
          fontSize: '14px',
          fontWeight: '600',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: '8px 0'
        }}
      >
        <Plus size={18} /> Add Another Item
      </button>
    </div>
  );

  // STEP 2: Vendor Selection
  const renderStep2 = () => (
    <div>
      {loadingVendors ? (
        <div style={styles.loadingContainer}>
          <Loader2 size={32} style={{ ...styles.spinning, color: 'var(--gold-primary)' }} />
          <div style={styles.loadingText}>Loading vendors...</div>
        </div>
      ) : (
        <MultiSelectDropdown
          label="Invited Vendors"
          required
          options={vendors}
          selected={formData.invitedVendors}
          onChange={(selected) => handleChange('invitedVendors', selected)}
          placeholder="Select vendors to invite"
          error={errors.invitedVendors}
          disabled={submitting}
        />
      )}
    </div>
  );

  // STEP 3: Timeline & Submission
  const renderStep3 = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <div style={styles.formGroup}>
        <label style={styles.formLabel}>
          Response Deadline
          <span style={styles.required}>*</span>
        </label>
        <div style={{ position: 'relative', maxWidth: '400px' }}>
          <input
            type="date"
            className="form-control"
            style={{
              borderColor: errors.responseDeadline ? '#dc2626' : undefined,
            }}
            value={formData.responseDeadline}
            onChange={(e) => handleChange('responseDeadline', e.target.value)}
            disabled={submitting}
            min={new Date().toISOString().split('T')[0]}
          />
        </div>
        {errors.responseDeadline && (
          <div style={styles.errorMessage}>
            <AlertCircle size={14} style={{ marginRight: '4px' }} />
            {errors.responseDeadline}
          </div>
        )}
      </div>

      {/* Step 3 content summary or placeholder removed for cleaner look */}
    </div>
  );

  const stepRenderers = [renderStep0, renderStep1, renderStep2, renderStep3];

  return (
    <>
      <PageBreadcrumb />

      <PageHeader
        title="Create RFP"
        description="Create a new Request for Proposal"
        actions={headerActions}
      />

      <div style={styles.pageContainer}>
        {/* Draft Notice Banner */}
        <div style={styles.infoBanner}>
          <AlertCircle size={18} style={{ marginRight: '12px', color: 'var(--gold-primary)' }} />
          <div>
            <strong>Draft Status:</strong> This RFP will be created as a draft. You can publish it later from the RFP management page.
          </div>
        </div>

        <div style={styles.card}>
          {/* Multi-step Header */}
          <FormStepper
            steps={STEPS}
            currentStep={currentStep}
            onStepClick={handleStepClick}
          />

          <div style={styles.cardBody}>
            <form onSubmit={currentStep === STEPS.length - 1 ? handleSubmit : handleNext}>
              {/* Animated Current Step */}
              <div key={currentStep} style={{ animation: 'fadeIn 0.25s ease-in' }}>
                {stepRenderers[currentStep]()}
              </div>

              {/* Navigation Buttons */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '32px 0 0 0',
                borderTop: '1px solid var(--border-color)',
                marginTop: '40px'
              }}>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="btn btn-secondary"
                    style={{ minWidth: '100px', borderRadius: '0', padding: '12px 24px' }}
                  >
                    <X size={16} />
                    Cancel
                  </button>
                  {currentStep > 0 && (
                    <button
                      type="button"
                      onClick={handleBack}
                      className="btn btn-secondary"
                      style={{
                        minWidth: '100px',
                        borderRadius: '0',
                        padding: '12px 24px',
                        border: '1px solid var(--gold-primary)',
                        color: 'var(--gold-primary)'
                      }}
                    >
                      <ChevronLeft size={16} />
                      Back
                    </button>
                  )}
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                  {/* Step dots */}
                  <div style={{ display: 'flex', gap: '6px' }}>
                    {STEPS.map((_, idx) => (
                      <div
                        key={idx}
                        style={{
                          width: idx === currentStep ? '20px' : '8px',
                          height: '8px',
                          borderRadius: '4px',
                          background: idx < currentStep ? 'var(--gold-bright)' : idx === currentStep ? 'var(--gold-primary)' : 'var(--divider-color)',
                          transition: 'all 0.3s ease',
                        }}
                      />
                    ))}
                  </div>

                  {currentStep < STEPS.length - 1 ? (
                    <button
                      type="submit"
                      className="btn btn-primary"
                      style={{
                        minWidth: '130px',
                        borderRadius: '0',
                        padding: '12px 28px',
                        backgroundColor: 'var(--gold-primary)',
                        border: 'none'
                      }}
                    >
                      Next
                      <ChevronRight size={16} />
                    </button>
                  ) : (
                    <button
                      type="submit"
                      disabled={submitting}
                      className="btn btn-primary"
                      style={{
                        minWidth: '160px',
                        borderRadius: '0',
                        padding: '12px 28px',
                        backgroundColor: submitting ? 'var(--text-muted)' : 'var(--gold-primary)',
                        border: 'none'
                      }}
                    >
                      {submitting ? (
                        <>
                          <Loader2 size={16} style={styles.spinning} />
                          Creating...
                        </>
                      ) : (
                        <>
                          <Save size={16} />
                          Create RFP
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>

      <ToastContainer toasts={toasts} removeToast={removeToast} />

      {/* Global Fade In Animation */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </>
  );
};

export default CreateRfp;